/**
 * Admin Dashboard
 * View and manage all users, signups, platform statistics, and emission factors
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../components/AuthProvider';
import AppLayout from '../components/layout/AppLayout';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Loader2, Users, BarChart3, Leaf, ArrowLeft, Plus, Pencil, Trash2, RotateCcw, Check, X, ChevronDown, ChevronRight } from 'lucide-react';
import {
  getUsers, getStats, deleteUser, setUserLimit, resetUserUsage,
  getEmissionFactors, updateEmissionFactor, createEmissionFactor, deleteEmissionFactor,
  getScope3Factors, updateScope3Factor, resetScope3FactorsToDefra,
} from '../services/adminService';

const TRANSPORT_MODES = ['road', 'rail', 'air', 'water'];

// Ordered so the tree renders in the same sequence as the DEFRA workbook.
const SCOPE3_MODE_ORDER = [
  'car', 'motorbike', 'taxi', 'bus', 'rail', 'ferry', 'flight', 'hotel', 'homeworking',
];
const SCOPE3_CAT_LABELS = { 6: 'Cat 6 · Business Travel', 7: 'Cat 7 · Employee Commuting' };

// Compose a human-readable variant label from the hierarchy fields on a row.
const scope3VariantLabel = (f) => {
  const parts = [];
  if (f.size_class) parts.push(f.size_class);
  if (f.vehicle_type && f.vehicle_type !== f.size_class) parts.push(f.vehicle_type);
  if (f.fuel_type) parts.push(f.fuel_type);
  if (f.cabin_class) parts.push(f.cabin_class);
  if (f.radiative_forcing === true) parts.push('with RF');
  if (f.radiative_forcing === false) parts.push('no RF');
  if (f.region && f.region !== 'UK') parts.push(f.region);
  return parts.join(' · ') || f.display_name || '—';
};

const AdminDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Users state
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingLimit, setEditingLimit] = useState(null);
  const [newLimit, setNewLimit] = useState('');

  // Emission factors state
  const [factors, setFactors] = useState([]);
  const [factorsLoading, setFactorsLoading] = useState(false);
  const [factorSearch, setFactorSearch] = useState('');
  const [factorModeFilter, setFactorModeFilter] = useState('all');
  const [editingFactor, setEditingFactor] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [newFactor, setNewFactor] = useState({
    transport_mode: 'road', category: '', subcategory: '', variant: '',
    emission_factor: '', unit: 'kgCO2e/tonne-km', display_name: '',
    per_km: false, source: 'UK DESNZ 2026 v1', year: 2026
  });

  // Scope 3 factors (Cat 6 + Cat 7) — edit-only admin surface
  const [scope3Factors, setScope3Factors] = useState([]);
  const [scope3Loading, setScope3Loading] = useState(false);
  const [scope3CatFilter, setScope3CatFilter] = useState('all');
  const [scope3Search, setScope3Search] = useState('');
  const [editingScope3Id, setEditingScope3Id] = useState(null);
  const [editScope3Values, setEditScope3Values] = useState({});
  const [expandedScope3Modes, setExpandedScope3Modes] = useState({});
  const [resettingScope3, setResettingScope3] = useState(false);

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/');
      return;
    }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [usersData, statsData] = await Promise.all([getUsers(), getStats()]);
      setUsers(usersData);
      setStats(statsData);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const fetchFactors = useCallback(async () => {
    try {
      setFactorsLoading(true);
      const mode = factorModeFilter === 'all' ? null : factorModeFilter;
      const data = await getEmissionFactors(mode);
      setFactors(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load emission factors');
    } finally {
      setFactorsLoading(false);
    }
  }, [factorModeFilter]);

  const fetchScope3Factors = useCallback(async () => {
    try {
      setScope3Loading(true);
      const ghgCategory = scope3CatFilter === 'all' ? null : parseInt(scope3CatFilter, 10);
      const data = await getScope3Factors({ ghgCategory });
      setScope3Factors(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load Scope 3 factors');
    } finally {
      setScope3Loading(false);
    }
  }, [scope3CatFilter]);

  // User handlers
  const handleDeleteUser = async (userId) => {
    try {
      await deleteUser(userId);
      setUsers(users.filter(u => u.id !== userId));
      toast.success('User deleted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleSetLimit = async (userId) => {
    const parsed = parseInt(newLimit, 10);
    if (Number.isNaN(parsed) || parsed < 0) {
      toast.error('Enter a valid non-negative limit');
      return;
    }
    try {
      const res = await setUserLimit(userId, parsed);
      // Trust the DB-confirmed values returned by the server, not the request.
      const confirmedLimit = res?.calculation_limit ?? parsed;
      setUsers(users.map(u => u.id === userId ? { ...u, calculation_limit: confirmedLimit } : u));
      setEditingLimit(null);
      setNewLimit('');
      toast.success(`Limit updated to ${confirmedLimit}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update limit');
    }
  };

  const handleResetUsage = async (userId) => {
    try {
      await resetUserUsage(userId);
      setUsers(users.map(u => u.id === userId ? { ...u, calculation_count: 0 } : u));
      toast.success('Usage reset');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset usage');
    }
  };

  // Emission factor handlers
  const handleEditFactor = (factor) => {
    setEditingFactor(factor.id);
    setEditValues({
      emission_factor: factor.emission_factor,
      display_name: factor.display_name,
      unit: factor.unit,
    });
  };

  const handleSaveFactor = async (factorId) => {
    try {
      const updated = await updateEmissionFactor(factorId, {
        emission_factor: parseFloat(editValues.emission_factor),
        display_name: editValues.display_name,
        unit: editValues.unit,
      });
      setFactors(factors.map(f => f.id === factorId ? updated : f));
      setEditingFactor(null);
      setEditValues({});
      toast.success('Factor updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update emission factor');
    }
  };

  const handleDeleteFactor = async (factorId) => {
    try {
      await deleteEmissionFactor(factorId);
      setFactors(factors.filter(f => f.id !== factorId));
      toast.success('Factor deleted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete emission factor');
    }
  };

  const handleAddFactor = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...newFactor,
        emission_factor: parseFloat(newFactor.emission_factor),
        subcategory: newFactor.subcategory || null,
        variant: newFactor.variant || null,
      };
      const created = await createEmissionFactor(data);
      setFactors([...factors, created]);
      setShowAddForm(false);
      setNewFactor({
        transport_mode: 'road', category: '', subcategory: '', variant: '',
        emission_factor: '', unit: 'kgCO2e/tonne-km', display_name: '',
        per_km: false, source: 'UK DESNZ 2026 v1', year: 2026
      });
      toast.success('Factor created');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create emission factor');
    }
  };

  // Scope 3 factor handlers (edit-only — no create/delete)
  const handleEditScope3 = (f) => {
    setEditingScope3Id(f.id);
    setEditScope3Values({
      display_name: f.display_name ?? '',
      co2e_total: f.co2e_total ?? '',
    });
  };

  const handleCancelScope3 = () => {
    setEditingScope3Id(null);
    setEditScope3Values({});
  };

  const handleSaveScope3 = async (factorId) => {
    const payload = {};
    const v = editScope3Values.co2e_total;
    if (v !== '' && v !== null && v !== undefined) {
      const n = parseFloat(v);
      if (Number.isNaN(n) || n < 0) {
        toast.error('co2e_total must be a non-negative number');
        return;
      }
      payload.co2e_total = n;
    }
    if (editScope3Values.display_name?.trim()) {
      payload.display_name = editScope3Values.display_name.trim();
    }
    try {
      const updated = await updateScope3Factor(factorId, payload);
      setScope3Factors(scope3Factors.map(f => f.id === factorId ? updated : f));
      handleCancelScope3();
      toast.success('Factor updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update Scope 3 factor');
    }
  };

  const handleResetScope3ToDefra = async () => {
    try {
      setResettingScope3(true);
      const result = await resetScope3FactorsToDefra();
      toast.success(`Reset complete — ${result.inserted} DEFRA rows restored`);
      await fetchScope3Factors();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset failed');
    } finally {
      setResettingScope3(false);
    }
  };

  const toggleScope3Mode = (key) => {
    setExpandedScope3Modes(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const filteredUsers = users.filter(u =>
    u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.company_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredFactors = factors.filter(f => {
    if (!factorSearch) return true;
    const q = factorSearch.toLowerCase();
    return (
      f.display_name?.toLowerCase().includes(q) ||
      f.category?.toLowerCase().includes(q) ||
      f.subcategory?.toLowerCase().includes(q) ||
      f.variant?.toLowerCase().includes(q)
    );
  });

  const groupedFactors = {};
  filteredFactors.forEach(f => {
    const mode = f.transport_mode;
    if (!groupedFactors[mode]) groupedFactors[mode] = [];
    groupedFactors[mode].push(f);
  });

  // Scope 3 filter + two-level grouping: mode → activity_group
  const filteredScope3 = scope3Factors.filter(f => {
    if (!scope3Search) return true;
    const q = scope3Search.toLowerCase();
    return (
      f.display_name?.toLowerCase().includes(q) ||
      f.mode?.toLowerCase().includes(q) ||
      f.vehicle_type?.toLowerCase().includes(q) ||
      f.fuel_type?.toLowerCase().includes(q) ||
      f.cabin_class?.toLowerCase().includes(q) ||
      f.region?.toLowerCase().includes(q) ||
      f.activity_group?.toLowerCase().includes(q)
    );
  });

  const scope3Tree = {}; // { mode: { activity_group: [factors] } }
  filteredScope3.forEach(f => {
    const mode = f.mode || 'unknown';
    const grp = f.activity_group || '—';
    if (!scope3Tree[mode]) scope3Tree[mode] = {};
    if (!scope3Tree[mode][grp]) scope3Tree[mode][grp] = [];
    scope3Tree[mode][grp].push(f);
  });
  const scope3ModesSorted = Object.keys(scope3Tree).sort(
    (a, b) => SCOPE3_MODE_ORDER.indexOf(a) - SCOPE3_MODE_ORDER.indexOf(b)
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  if (!user?.is_admin) return null;

  return (
    <AppLayout title="Admin Dashboard">
      <div className="max-w-7xl mx-auto">
        <Tabs
          defaultValue="users"
          onValueChange={(v) => {
            if (v === 'factors') fetchFactors();
            if (v === 'scope3') fetchScope3Factors();
          }}
        >
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <TabsTrigger value="users">
              <Users className="w-4 h-4 mr-2" />
              Users
            </TabsTrigger>
            <TabsTrigger value="factors">
              <BarChart3 className="w-4 h-4 mr-2" />
              Freight Factors
            </TabsTrigger>
            <TabsTrigger value="scope3">
              <Leaf className="w-4 h-4 mr-2" />
              Scope 3 Factors
            </TabsTrigger>
          </TabsList>

          {/* ==================== USERS TAB ==================== */}
          <TabsContent value="users">
            {/* Stats Cards */}
            {stats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <Card>
                  <CardContent className="p-5">
                    <div className="text-xs font-medium text-muted-foreground">Total Users</div>
                    <div className="mt-1.5 text-2xl font-semibold tracking-tight tabular-nums text-foreground">{(stats.total_users ?? 0).toLocaleString()}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-5">
                    <div className="text-xs font-medium text-muted-foreground">Verified Users</div>
                    <div className="mt-1.5 text-2xl font-semibold tracking-tight tabular-nums text-foreground">{(stats.verified_users ?? 0).toLocaleString()}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-5">
                    <div className="text-xs font-medium text-muted-foreground">Total Calculations</div>
                    <div className="mt-1.5 text-2xl font-semibold tracking-tight tabular-nums text-foreground">{(stats.total_calculations ?? 0).toLocaleString()}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-5">
                    <div className="text-xs font-medium text-muted-foreground">Total Shipments</div>
                    <div className="mt-1.5 text-2xl font-semibold tracking-tight tabular-nums text-foreground">{(stats.total_shipments ?? 0).toLocaleString()}</div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Search */}
            <div className="mb-4">
              <Input
                type="text"
                placeholder="Search by email, name, or company..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full md:w-96"
              />
            </div>

            {/* Users Table */}
            <Card>
              <CardHeader className="pb-3 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100">
                <CardTitle className="text-base font-semibold text-gray-800">
                  Users ({filteredUsers.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {loading ? (
                  <div className="p-8 flex items-center justify-center gap-2 text-gray-500">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Loading users...
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Signed Up</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Usage</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {filteredUsers.map((u) => (
                          <tr key={u.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3">
                              <div className="font-medium text-gray-900">{u.email}</div>
                              <div className="text-xs text-gray-500">{u.full_name || 'No name'}</div>
                            </td>
                            <td className="px-4 py-3 text-gray-600">{u.company_name || '-'}</td>
                            <td className="px-4 py-3 text-gray-600">{formatDate(u.created_at)}</td>
                            <td className="px-4 py-3">
                              {editingLimit === u.id ? (
                                <div className="flex items-center gap-2">
                                  <Input
                                    type="number"
                                    value={newLimit}
                                    onChange={(e) => setNewLimit(e.target.value)}
                                    className="w-20 h-8 text-sm"
                                    placeholder={String(u.calculation_limit)}
                                  />
                                  <Button size="sm" variant="ghost" onClick={() => handleSetLimit(u.id)}>
                                    <Check className="w-3.5 h-3.5 text-green-600" />
                                  </Button>
                                  <Button size="sm" variant="ghost" onClick={() => { setEditingLimit(null); setNewLimit(''); }}>
                                    <X className="w-3.5 h-3.5 text-gray-400" />
                                  </Button>
                                </div>
                              ) : (
                                <button
                                  className="hover:text-sky-600 transition-colors"
                                  onClick={() => { setEditingLimit(u.id); setNewLimit(u.calculation_limit.toString()); }}
                                >
                                  <span className="font-medium">{u.calculation_count}</span>
                                  <span className="text-gray-400"> / {u.calculation_limit}</span>
                                </button>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-1">
                                <Badge variant={u.calculation_count >= u.calculation_limit ? 'destructive' : 'default'} className="text-[10px]">
                                  {u.calculation_count >= u.calculation_limit ? 'Limit Reached' : 'Active'}
                                </Badge>
                                {u.is_admin && (
                                  <Badge variant="secondary" className="text-[10px]">Admin</Badge>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-1">
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button size="sm" variant="ghost" className="text-sky-600 hover:text-sky-800 h-7 px-2">
                                      <RotateCcw className="w-3.5 h-3.5 mr-1" />
                                      Reset
                                    </Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>Reset Usage</AlertDialogTitle>
                                      <AlertDialogDescription>
                                        Reset {u.email}'s calculation count to 0?
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                                      <AlertDialogAction onClick={() => handleResetUsage(u.id)}>
                                        Reset
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                                {!u.is_admin && (
                                  <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                      <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-800 h-7 px-2">
                                        <Trash2 className="w-3.5 h-3.5 mr-1" />
                                        Delete
                                      </Button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent>
                                      <AlertDialogHeader>
                                        <AlertDialogTitle>Delete User</AlertDialogTitle>
                                        <AlertDialogDescription>
                                          Delete user {u.email}? This cannot be undone.
                                        </AlertDialogDescription>
                                      </AlertDialogHeader>
                                      <AlertDialogFooter>
                                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                                        <AlertDialogAction
                                          onClick={() => handleDeleteUser(u.id)}
                                          className="bg-red-600 hover:bg-red-700"
                                        >
                                          Delete
                                        </AlertDialogAction>
                                      </AlertDialogFooter>
                                    </AlertDialogContent>
                                  </AlertDialog>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {filteredUsers.length === 0 && (
                      <div className="p-8 text-center text-gray-400">
                        {searchTerm ? 'No users match your search' : 'No users found'}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ==================== EMISSION FACTORS TAB ==================== */}
          <TabsContent value="factors">
            {/* Controls */}
            <div className="flex flex-col md:flex-row gap-3 mb-4">
              <Input
                type="text"
                placeholder="Search emission factors..."
                value={factorSearch}
                onChange={(e) => setFactorSearch(e.target.value)}
                className="flex-1 md:max-w-sm"
              />
              <Select value={factorModeFilter} onValueChange={setFactorModeFilter}>
                <SelectTrigger className="w-full md:w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Transport Modes</SelectItem>
                  {TRANSPORT_MODES.map(m => (
                    <SelectItem key={m} value={m}>{m.charAt(0).toUpperCase() + m.slice(1)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                onClick={() => setShowAddForm(!showAddForm)}
                variant={showAddForm ? 'outline' : 'default'}
                className={showAddForm ? '' : 'bg-sky-600 hover:bg-sky-700'}
              >
                {showAddForm ? (
                  <>
                    <X className="w-4 h-4 mr-2" />
                    Cancel
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    Add New Factor
                  </>
                )}
              </Button>
            </div>

            {/* Add Form */}
            {showAddForm && (
              <Card className="mb-4">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base font-semibold">Add New Emission Factor</CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleAddFactor}>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <Label className="text-xs">Transport Mode*</Label>
                        <Select value={newFactor.transport_mode} onValueChange={(v) => setNewFactor({...newFactor, transport_mode: v})}>
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {TRANSPORT_MODES.map(m => (
                              <SelectItem key={m} value={m}>{m.charAt(0).toUpperCase() + m.slice(1)}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">Category*</Label>
                        <Input
                          value={newFactor.category}
                          onChange={(e) => setNewFactor({...newFactor, category: e.target.value})}
                          placeholder="e.g., van, hgv, tanker"
                          className="mt-1"
                          required
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Subcategory</Label>
                        <Input
                          value={newFactor.subcategory}
                          onChange={(e) => setNewFactor({...newFactor, subcategory: e.target.value})}
                          placeholder="e.g., diesel, crude"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Variant</Label>
                        <Input
                          value={newFactor.variant}
                          onChange={(e) => setNewFactor({...newFactor, variant: e.target.value})}
                          placeholder="e.g., average, class_i"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Emission Factor*</Label>
                        <Input
                          type="number"
                          step="any"
                          value={newFactor.emission_factor}
                          onChange={(e) => setNewFactor({...newFactor, emission_factor: e.target.value})}
                          placeholder="0.00000"
                          className="mt-1"
                          required
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Unit*</Label>
                        <Select value={newFactor.unit} onValueChange={(v) => setNewFactor({...newFactor, unit: v})}>
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="kgCO2e/tonne-km">kgCO2e/tonne-km</SelectItem>
                            <SelectItem value="kgCO2e/km">kgCO2e/km</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="md:col-span-2">
                        <Label className="text-xs">Display Name*</Label>
                        <Input
                          value={newFactor.display_name}
                          onChange={(e) => setNewFactor({...newFactor, display_name: e.target.value})}
                          placeholder="e.g., Rigid (>3.5-7.5t) - Average"
                          className="mt-1"
                          required
                        />
                      </div>
                      <div className="flex items-end">
                        <label className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={newFactor.per_km}
                            onChange={(e) => setNewFactor({...newFactor, per_km: e.target.checked})}
                            className="rounded"
                          />
                          Per km (not per tonne-km)
                        </label>
                      </div>
                    </div>
                    <div className="mt-3 flex justify-end">
                      <Button type="submit" className="bg-green-600 hover:bg-green-700">
                        Create Factor
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            )}

            {/* Factors Table */}
            <Card>
              <CardHeader className="pb-3 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100">
                <CardTitle className="text-base font-semibold text-gray-800">
                  Emission Factors ({filteredFactors.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {factorsLoading ? (
                  <div className="p-8 flex items-center justify-center gap-2 text-gray-500">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Loading emission factors...
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    {Object.keys(groupedFactors).sort().map(mode => (
                      <div key={mode}>
                        <div className="px-4 py-2 bg-gray-100 border-b border-t">
                          <span className="text-sm font-semibold text-gray-700 uppercase">{mode}</span>
                          <span className="ml-2 text-xs text-gray-400">({groupedFactors[mode].length} factors)</span>
                        </div>
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50 border-b">
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Subcategory</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Variant</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Factor</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Unit</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {groupedFactors[mode].map((f) => (
                              <tr key={f.id} className="hover:bg-gray-50">
                                <td className="px-4 py-2">
                                  {editingFactor === f.id ? (
                                    <Input
                                      value={editValues.display_name}
                                      onChange={(e) => setEditValues({...editValues, display_name: e.target.value})}
                                      className="h-8 text-sm"
                                    />
                                  ) : (
                                    <span className="font-medium text-gray-900">{f.display_name}</span>
                                  )}
                                </td>
                                <td className="px-4 py-2 text-gray-600">{f.category}</td>
                                <td className="px-4 py-2 text-gray-600">{f.subcategory || '-'}</td>
                                <td className="px-4 py-2 text-gray-600">{f.variant || '-'}</td>
                                <td className="px-4 py-2">
                                  {editingFactor === f.id ? (
                                    <Input
                                      type="number"
                                      step="any"
                                      value={editValues.emission_factor}
                                      onChange={(e) => setEditValues({...editValues, emission_factor: e.target.value})}
                                      className="w-28 h-8 text-sm"
                                    />
                                  ) : (
                                    <span className="font-mono text-gray-900">{f.emission_factor}</span>
                                  )}
                                </td>
                                <td className="px-4 py-2 text-xs text-gray-500">
                                  {editingFactor === f.id ? (
                                    <Select value={editValues.unit} onValueChange={(v) => setEditValues({...editValues, unit: v})}>
                                      <SelectTrigger className="h-8 text-xs">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="kgCO2e/tonne-km">kgCO2e/tonne-km</SelectItem>
                                        <SelectItem value="kgCO2e/km">kgCO2e/km</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  ) : (
                                    f.unit
                                  )}
                                </td>
                                <td className="px-4 py-2">
                                  {editingFactor === f.id ? (
                                    <div className="flex items-center gap-1">
                                      <Button size="sm" variant="ghost" onClick={() => handleSaveFactor(f.id)} className="h-7 px-2 text-green-600 hover:text-green-800">
                                        <Check className="w-3.5 h-3.5 mr-1" />
                                        Save
                                      </Button>
                                      <Button size="sm" variant="ghost" onClick={() => { setEditingFactor(null); setEditValues({}); }} className="h-7 px-2">
                                        <X className="w-3.5 h-3.5 mr-1" />
                                        Cancel
                                      </Button>
                                    </div>
                                  ) : (
                                    <div className="flex items-center gap-1">
                                      <Button size="sm" variant="ghost" onClick={() => handleEditFactor(f)} className="h-7 px-2 text-sky-600 hover:text-sky-800">
                                        <Pencil className="w-3.5 h-3.5 mr-1" />
                                        Edit
                                      </Button>
                                      <AlertDialog>
                                        <AlertDialogTrigger asChild>
                                          <Button size="sm" variant="ghost" className="h-7 px-2 text-red-600 hover:text-red-800">
                                            <Trash2 className="w-3.5 h-3.5 mr-1" />
                                            Delete
                                          </Button>
                                        </AlertDialogTrigger>
                                        <AlertDialogContent>
                                          <AlertDialogHeader>
                                            <AlertDialogTitle>Delete Emission Factor</AlertDialogTitle>
                                            <AlertDialogDescription>
                                              Delete "{f.display_name}"? This cannot be undone.
                                            </AlertDialogDescription>
                                          </AlertDialogHeader>
                                          <AlertDialogFooter>
                                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                                            <AlertDialogAction
                                              onClick={() => handleDeleteFactor(f.id)}
                                              className="bg-red-600 hover:bg-red-700"
                                            >
                                              Delete
                                            </AlertDialogAction>
                                          </AlertDialogFooter>
                                        </AlertDialogContent>
                                      </AlertDialog>
                                    </div>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ))}
                    {filteredFactors.length === 0 && (
                      <div className="p-8 text-center text-gray-400">
                        {factorSearch ? 'No emission factors match your search' : 'No emission factors found'}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ==================== SCOPE 3 FACTORS TAB ==================== */}
          <TabsContent value="scope3">
            <div className="mb-3 text-sm text-gray-600">
              UK DESNZ GHG Conversion Factors 2026 · Cat 6 (Business Travel) + Cat 7 (Employee Commuting).
              Edit the numeric values below. Hierarchy fields (mode / vehicle / fuel) are read-only —
              to add or remove factors, edit <code className="px-1 py-0.5 bg-gray-100 rounded text-xs">backend/scope3_factors_seed.py</code> and click Reset.
            </div>

            <div className="mb-4 flex flex-wrap items-center gap-3">
              <Input
                type="text"
                placeholder="Search mode, vehicle, fuel, region..."
                value={scope3Search}
                onChange={(e) => setScope3Search(e.target.value)}
                className="w-full md:w-80"
              />
              <Select value={scope3CatFilter} onValueChange={setScope3CatFilter}>
                <SelectTrigger className="w-56">
                  <SelectValue placeholder="All categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All categories</SelectItem>
                  <SelectItem value="6">Cat 6 · Business Travel</SelectItem>
                  <SelectItem value="7">Cat 7 · Employee Commuting</SelectItem>
                </SelectContent>
              </Select>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="outline"
                    disabled={resettingScope3}
                    className="text-amber-700 border-amber-300 hover:bg-amber-50"
                  >
                    {resettingScope3 ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <RotateCcw className="w-4 h-4 mr-2" />
                    )}
                    Reset to DEFRA defaults
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Reset all Scope 3 factors?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This reseeds Cat 6 + Cat 7 factors from the committed DEFRA baseline
                      (<code className="px-1 bg-gray-100 rounded">backend/scope3_factors_seed.py</code>).
                      Any admin edits will be overwritten. This cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleResetScope3ToDefra}
                      className="bg-amber-600 hover:bg-amber-700"
                    >
                      Reset to DEFRA
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>

            <Card>
              <CardHeader className="pb-3 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100">
                <CardTitle className="text-base font-semibold text-gray-800">
                  Scope 3 Factors ({filteredScope3.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {scope3Loading ? (
                  <div className="p-8 flex items-center justify-center gap-2 text-gray-500">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Loading Scope 3 factors...
                  </div>
                ) : scope3ModesSorted.length === 0 ? (
                  <div className="p-8 text-center text-gray-400">
                    {scope3Search
                      ? 'No Scope 3 factors match your search'
                      : 'No Scope 3 factors in the database. Click "Reset to DEFRA defaults" to seed.'}
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    {scope3ModesSorted.map(mode => {
                      const groups = scope3Tree[mode];
                      const modeCount = Object.values(groups).reduce((n, arr) => n + arr.length, 0);
                      const isOpen = expandedScope3Modes[mode] !== false; // default open
                      return (
                        <div key={mode}>
                          <button
                            onClick={() => toggleScope3Mode(mode)}
                            className="w-full flex items-center gap-2 px-4 py-2 bg-gray-100 border-b border-t text-left hover:bg-gray-200 transition-colors"
                          >
                            {isOpen
                              ? <ChevronDown className="w-4 h-4 text-gray-500" />
                              : <ChevronRight className="w-4 h-4 text-gray-500" />}
                            <span className="text-sm font-semibold text-gray-700 uppercase">{mode}</span>
                            <span className="ml-2 text-xs text-gray-400">({modeCount} factors)</span>
                            <Badge variant="outline" className="ml-auto text-xs">
                              {SCOPE3_CAT_LABELS[groups[Object.keys(groups)[0]][0].ghg_category] || `Cat ${groups[Object.keys(groups)[0]][0].ghg_category}`}
                            </Badge>
                          </button>
                          {isOpen && Object.keys(groups).sort().map(groupName => (
                            <div key={`${mode}::${groupName}`}>
                              <div className="px-6 py-1.5 bg-gray-50 text-xs font-medium text-gray-500">
                                {groupName}
                              </div>
                              <table className="w-full text-sm">
                                <thead className="bg-white border-b">
                                  <tr>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Variant</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Unit</th>
                                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">co2e total</th>
                                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                  {groups[groupName].map(f => {
                                    const isEditing = editingScope3Id === f.id;
                                    return (
                                      <tr key={f.id} className="hover:bg-gray-50">
                                        <td className="px-4 py-2">
                                          {isEditing ? (
                                            <Input
                                              value={editScope3Values.display_name}
                                              onChange={(e) => setEditScope3Values({ ...editScope3Values, display_name: e.target.value })}
                                              className="h-8 text-sm"
                                              placeholder="Display name"
                                            />
                                          ) : (
                                            <>
                                              <div className="font-medium text-gray-900">{scope3VariantLabel(f)}</div>
                                              <div className="text-xs text-gray-400">{f.display_name}</div>
                                            </>
                                          )}
                                        </td>
                                        <td className="px-4 py-2 text-xs text-gray-500">{f.activity_unit || '-'}</td>
                                        <td className="px-4 py-2 text-right font-mono">
                                          {isEditing ? (
                                            <Input
                                              type="number"
                                              step="any"
                                              value={editScope3Values.co2e_total}
                                              onChange={(e) => setEditScope3Values({ ...editScope3Values, co2e_total: e.target.value })}
                                              className="w-28 h-8 text-xs ml-auto"
                                            />
                                          ) : (
                                            <span>{f.co2e_total ?? '—'}</span>
                                          )}
                                        </td>
                                        <td className="px-4 py-2 text-right">
                                          {isEditing ? (
                                            <div className="flex items-center justify-end gap-1">
                                              <Button size="sm" variant="ghost" onClick={() => handleSaveScope3(f.id)} className="h-7 px-2 text-green-600 hover:text-green-800">
                                                <Check className="w-3.5 h-3.5 mr-1" />
                                                Save
                                              </Button>
                                              <Button size="sm" variant="ghost" onClick={handleCancelScope3} className="h-7 px-2">
                                                <X className="w-3.5 h-3.5 mr-1" />
                                                Cancel
                                              </Button>
                                            </div>
                                          ) : (
                                            <Button size="sm" variant="ghost" onClick={() => handleEditScope3(f)} className="h-7 px-2 text-sky-600 hover:text-sky-800">
                                              <Pencil className="w-3.5 h-3.5 mr-1" />
                                              Edit
                                            </Button>
                                          )}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AppLayout>
  );
};

export default AdminDashboard;
